import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BellRing } from 'lucide-react';
import { api, type PushDevice } from '../../lib/api';
import { formatDateTime } from '../../lib/format';

/**
 * The devices enrolled for alerts (decision D41, D7 of the DEBT-7 plan).
 *
 * On the same screen as the session list deliberately. Somebody looking at "the phones
 * that can reach my account" does not distinguish a device that receives alerts from a
 * device with a live session, and two separate lists of the same handsets — one per
 * feature — would be worse than either. The labels are derived the same way on both sides
 * (`SessionService.describeDevice` mirrors `pushManager.describeDevice`) so one phone
 * reads identically in both.
 *
 * `GET /push/devices` and `DELETE /push/devices` have existed since Phase 4 with no
 * caller at all — D39 found them and deliberately left them for this screen.
 */
export function PushDeviceList() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<PushDevice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api
      .listPushDevices()
      .then((next) => {
        setRows(next);
        setError(null);
      })
      .catch(() => setError(t('settings.alerts.loadFailed')));
  }, [t]);

  useEffect(load, [load]);

  const forgetAll = useCallback(async () => {
    setBusy(true);
    try {
      await api.forgetAllPushDevices();
      load();
    } catch {
      setError(t('settings.alerts.forgetFailed'));
    } finally {
      setBusy(false);
    }
  }, [load, t]);

  if (error && !rows) {
    return (
      <p role="alert" className="px-4 py-3 text-sm text-red-400">
        {error}
      </p>
    );
  }
  if (!rows) return <p className="px-4 py-3 text-sm text-slate-500">{t('settings.loading')}</p>;
  if (rows.length === 0) {
    return <p className="px-4 py-3 text-sm text-slate-500">{t('settings.alerts.empty')}</p>;
  }

  return (
    <div>
      {error && (
        <p role="alert" className="px-4 pb-2 text-sm text-red-400">
          {error}
        </p>
      )}
      <ul className="divide-y divide-slate-800">
        {rows.map((device) => (
          <li key={device.id} className="flex items-start gap-3 px-4 py-3">
            <BellRing className="mt-0.5 size-4 shrink-0 text-slate-500" aria-hidden="true" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-100">
                {device.deviceName ?? t('settings.sessions.unknownDevice')}
              </p>
              <p className="mt-0.5 text-xs text-slate-400">
                {device.lastNotifiedAt
                  ? t('settings.alerts.lastAlert', {
                      at: formatDateTime(device.lastNotifiedAt),
                    })
                  : t('settings.alerts.neverAlerted')}
              </p>
            </div>
          </li>
        ))}
      </ul>

      {/* All of them, not one: the server offers no per-device delete, and a button that
          implied otherwise would be a lie about what the tap does. */}
      <div className="px-4 pb-4 pt-3">
        <button
          type="button"
          onClick={() => void forgetAll()}
          disabled={busy}
          className="min-h-[48px] w-full rounded-lg border border-slate-700 bg-transparent px-4 text-sm font-medium text-slate-200 disabled:opacity-50"
        >
          {t('settings.alerts.forgetAll')}
        </button>
        <p className="mt-2 text-xs text-slate-500">{t('settings.alerts.forgetAllNote')}</p>
      </div>
    </div>
  );
}
